import { GeoPositionError, getCurrentPosition } from '../utils/geolocation';

/**
 * Caches and de-duplicates position lookups for callers that ask repeatedly
 * (proximity filters, list scoring). The actual read is delegated to
 * `getCurrentPosition`, which picks the native plugin or the browser API —
 * keeping Capacitor knowledge in exactly one place (PD-14866).
 */
class GeolocationServiceImpl {
	private _cachedPosition: { lat: number; lng: number } | null = null;
	private _pendingRequest: Promise<{
		lat: number;
		lng: number;
	} | null> | null = null;
	private _error: string | null = null;

	public getCachedPosition(): { lat: number; lng: number } | null {
		return this._cachedPosition;
	}

	public getError(): string | null {
		return this._error;
	}

	public async requestPosition(): Promise<{
		lat: number;
		lng: number;
	} | null> {
		if (this._cachedPosition != null) {
			return this._cachedPosition;
		}
		if (this._pendingRequest != null) {
			return this._pendingRequest;
		}
		this._pendingRequest = getCurrentPosition({
			enableHighAccuracy: false,
			timeout: 10000,
		})
			.then(({ latitude, longitude }) => {
				this._cachedPosition = { lat: latitude, lng: longitude };
				this._error = null;
				return this._cachedPosition;
			})
			.catch((err: unknown) => {
				// This service owns its own error wording — the underlying helper
				// is shared with callers that phrase failures differently.
				this._error =
					err instanceof GeoPositionError && err.reason === 'unsupported'
						? 'Geolocation is not supported by this browser'
						: err instanceof Error
							? err.message
							: 'Geolocation is not available on this device';
				return null;
			});
		const result = await this._pendingRequest;
		this._pendingRequest = null;
		return result;
	}
}

export const GeolocationService = new GeolocationServiceImpl();
